import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, UserCheck, Calendar, Clock, Car, Filter } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Visitors.module.css';

const Visitors = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    purpose: 'Guest',
    expectedDate: '',
    expectedTime: '',
    vehicleNumber: ''
  });
  const [photoFile, setPhotoFile] = useState(null);

  useEffect(() => {
    fetchVisitors();
  }, []);

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users/visitors');
      setVisitors(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch visitors');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setPhotoFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => data.append(key, formData[key]));
      if (photoFile) {
        data.append('photo', photoFile);
      }

      const res = await api.post('/users/visitors', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVisitors([res.data, ...visitors]);
      setFormData({ name: '', phone: '', purpose: 'Guest', expectedDate: '', expectedTime: '', vehicleNumber: '' });
      setPhotoFile(null);
      setIsDrawerOpen(false);
      toast.success('Visitor pre-approved successfully!');
    } catch (error) {
      toast.error('Failed to pre-approve visitor');
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="My Visitors" 
        breadcrumb={['Services', 'Visitors']} 
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Pre-approve Visitor
          </button>
        }
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <input type="text" placeholder="Search visitors..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Visitor Name</th>
                <th>Purpose</th>
                <th>Expected Schedule</th>
                <th>Vehicle No.</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className={styles.emptyState}>Loading visitors...</td>
                </tr>
              ) : visitors.map(v => (
                <tr key={v._id}>
                  <td>
                    <div className={styles.visitorName}>
                      <div className={styles.avatarSmall}>{v.name.charAt(0)}</div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <strong>{v.name}</strong>
                        {v.phone && <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 'normal', marginTop: '2px' }}>{v.phone}</span>}
                      </div>
                    </div>
                  </td>
                  <td><span className={styles.badgePurpose}>{v.purpose}</span></td>
                  <td>
                    <div className={styles.scheduleInfo}>
                      <span><Calendar size={12} /> {new Date(v.expectedDate).toLocaleDateString('en-GB')}</span>
                      <span><Clock size={12} /> {v.expectedTime}</span>
                    </div>
                  </td>
                  <td>
                    {v.vehicleNumber && v.vehicleNumber !== 'N/A' ? (
                      <div className={styles.vehicleInfo}><Car size={14} /> {v.vehicleNumber}</div>
                    ) : '-'}
                  </td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[v.status.replace(' ', '').toLowerCase()]}`}>
                      {v.status === 'Expected' && <Clock size={12} />}
                      {v.status === 'Checked In' && <UserCheck size={12} />}
                      {v.status}
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && visitors.length === 0 && (
                <tr>
                  <td colSpan="5" className={styles.emptyState}>No visitors found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileList}>
          {loading ? (
             <div className={styles.emptyState}>Loading visitors...</div>
          ) : visitors.length === 0 ? (
             <div className={styles.emptyState}>No visitors found.</div>
          ) : visitors.map(v => (
            <div key={v._id} className={styles.mobileCard}>
               <div className={styles.mobileCardHeader}>
                 <div className={styles.visitorName}>
                   <div className={styles.avatarSmall}>{v.name.charAt(0)}</div>
                   <div style={{ display: 'flex', flexDirection: 'column' }}>
                     <strong>{v.name}</strong>
                     {v.phone && <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 'normal', marginTop: '2px' }}>{v.phone}</span>}
                   </div>
                 </div>
                 <span className={`${styles.badgeStatus} ${styles[v.status.replace(' ', '').toLowerCase()]}`}>
                    {v.status === 'Expected' && <Clock size={12} />}
                    {v.status === 'Checked In' && <UserCheck size={12} />}
                    {v.status}
                 </span>
               </div>
               <div className={styles.mobileCardBody}>
                 <div className={styles.mobileMeta}>
                   <span className={styles.badgePurpose}>{v.purpose}</span>
                   {v.vehicleNumber && v.vehicleNumber !== 'N/A' && (
                     <div className={styles.vehicleInfo}><Car size={14} /> {v.vehicleNumber}</div>
                   )}
                 </div>
                 <div className={styles.scheduleInfo}>
                   <span><Calendar size={12} /> {new Date(v.expectedDate).toLocaleDateString('en-GB')}</span>
                   <span><Clock size={12} /> {v.expectedTime}</span>
                 </div>
               </div>
            </div>
          ))}
        </div>
      </div>

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title="Pre-approve Visitor"
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Visitor/Company Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. Ramesh or Amazon" />
          </div>
          <div className="formGroup">
            <label>Contact Number (Optional)</label>
            <input name="phone" value={formData.phone} onChange={handleChange} placeholder="10-digit number" />
          </div>
          <div className="formGroup">
            <label>Visitor Photo (Optional)</label>
            <input type="file" name="photo" accept="image/*" onChange={handleFileChange} />
          </div>
          <div className="formGroup">
            <label>Purpose of Visit</label>
            <select name="purpose" value={formData.purpose} onChange={handleChange}>
              <option value="Guest">Guest / Relative</option>
              <option value="Delivery">Delivery</option>
              <option value="Cab">Cab</option>
              <option value="Maintenance">Maintenance Worker</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Expected Date</label>
            <input type="date" name="expectedDate" value={formData.expectedDate} onChange={handleChange} required />
          </div>
          <div className="formGroup">
            <label>Expected Time</label>
            <input type="time" name="expectedTime" value={formData.expectedTime} onChange={handleChange} required />
          </div>
          <div className="formGroup">
            <label>Vehicle Number (Optional)</label>
            <input name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} placeholder="e.g. MH 12 AB 1234" />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Pre-Approve</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Visitors;
